// Schedule-stability chip — surfaces carrier_schedule_volatility (the sailing_revisions signal): how
// often / how far a carrier rewrites its OWN published schedule before sailing. A planning-reliability
// dimension distinct from on-time %. Desk voice, AA-safe tones. Renders nothing until there's enough
// data to judge (level === null = early data).

export interface CarrierVolatility {
  level: 'low' | 'medium' | 'high' | null
  revised_sailings: number
  active_sailings: number
  mean_abs_etd_hours: number | null
  late_etd_share: number | null
  etd_revisions: number
}

const LABEL: Record<'low' | 'medium' | 'high', string> = {
  low: 'stable schedule',
  medium: 'some schedule churn',
  high: 'high schedule churn',
}
// green = stable (good), amber = some, orange = heavy churn (bad).
const TONE: Record<'low' | 'medium' | 'high', string> = {
  low: 'text-signal bg-signal/10',
  medium: 'text-warn bg-warn/10',
  high: 'text-overdue bg-overdue/10',
}

export function VolatilityChip({ v }: { v: CarrierVolatility | null | undefined }) {
  if (!v || !v.level) return null
  const pct =
    v.active_sailings > 0 ? Math.round((v.revised_sailings / v.active_sailings) * 100) : null
  const days = v.mean_abs_etd_hours != null ? (v.mean_abs_etd_hours / 24).toFixed(1) : null
  const late = v.late_etd_share != null ? Math.round(v.late_etd_share * 100) : null
  const detail = [
    pct != null ? `revises ~${pct}% of sailings` : null,
    days != null ? `avg ±${days}d` : null,
    late != null ? `${late}% last-minute` : null,
  ]
    .filter(Boolean)
    .join(' · ')
  return (
    <span
      className={`text-[10px] font-mono uppercase tracking-wide rounded px-1.5 py-0.5 whitespace-nowrap ${TONE[v.level]}`}
      title={`Schedule stability (last 90d): ${detail || 'early data'}`}
    >
      {LABEL[v.level]}
    </span>
  )
}
